import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { 
  Bell, Plus, Trash2, Save, AlertTriangle, Thermometer,
  Droplets, Wind, CloudRain, Sun, Snowflake, Cloud,
  Check, X, ChevronDown, MapPin
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALERT_TYPES = [
  { id: "temp_high", name: "High Temperature", icon: Thermometer, unit: "°F", color: "text-red-400" },
  { id: "temp_low", name: "Low Temperature", icon: Thermometer, unit: "°F", color: "text-blue-400" },
  { id: "rain", name: "Rain Probability", icon: CloudRain, unit: "%", color: "text-cyan-400" },
  { id: "wind", name: "Wind Speed", icon: Wind, unit: "mph", color: "text-gray-400" },
  { id: "humidity", name: "Humidity", icon: Droplets, unit: "%", color: "text-blue-300" },
  { id: "uv", name: "UV Index", icon: Sun, unit: "", color: "text-yellow-400" },
  { id: "snow", name: "Snow Probability", icon: Snowflake, unit: "%", color: "text-white" },
  { id: "aqi", name: "Air Quality Index", icon: Cloud, unit: "", color: "text-purple-400" },
];

const OPERATORS = [
  { id: "gt", label: "Greater than", symbol: ">" },
  { id: "lt", label: "Less than", symbol: "<" },
  { id: "eq", label: "Equals", symbol: "=" },
  { id: "gte", label: "Greater or equal", symbol: "≥" },
  { id: "lte", label: "Less or equal", symbol: "≤" },
];

export default function CustomAlerts({ token, isDarkMode, savedLocations = [] }) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newAlert, setNewAlert] = useState({
    type: "temp_high",
    operator: "gt",
    value: "",
    location: "",
    enabled: true,
    notify_email: true,
    notify_push: false
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (token) {
      fetchAlerts();
    }
  }, [token]);

  const fetchAlerts = async () => {
    try {
      const response = await axios.get(`${API}/alerts/custom`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAlerts(response.data.alerts || []);
    } catch (error) {
      console.error("Failed to fetch custom alerts:", error);
    } finally {
      setLoading(false);
    }
  };

  const createAlert = async () => {
    if (!newAlert.value) {
      toast.error("Please enter a threshold value");
      return;
    }

    try {
      const response = await axios.post(`${API}/alerts/custom`, newAlert, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAlerts([...alerts, response.data.alert]);
      setShowAddForm(false);
      setNewAlert({
        type: "temp_high",
        operator: "gt",
        value: "",
        location: "",
        enabled: true,
        notify_email: true,
        notify_push: false
      });
      toast.success("Alert created successfully!");
    } catch (error) {
      toast.error("Failed to create alert");
    }
  };

  const deleteAlert = async (alertId) => {
    try {
      await axios.delete(`${API}/alerts/custom/${alertId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAlerts(alerts.filter(a => a.id !== alertId));
      toast.success("Alert deleted");
    } catch (error) {
      toast.error("Failed to delete alert");
    }
  };

  const toggleAlert = async (alertId, enabled) => {
    try {
      await axios.patch(`${API}/alerts/custom/${alertId}`, { enabled }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAlerts(alerts.map(a => a.id === alertId ? { ...a, enabled } : a));
    } catch (error) {
      toast.error("Failed to update alert");
    }
  };

  const getAlertType = (typeId) => ALERT_TYPES.find(t => t.id === typeId) || ALERT_TYPES[0];
  const getOperator = (opId) => OPERATORS.find(o => o.id === opId) || OPERATORS[0];

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-12 bg-white/10 rounded-xl"></div>
        <div className="h-24 bg-white/10 rounded-xl"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Bell className="w-5 h-5 text-[#ff0080]" />
          <h3 className="font-bold">Custom Weather Alerts</h3>
        </div>
        <Button 
          size="sm" 
          onClick={() => setShowAddForm(true)}
          className="bg-[#00f2ea] text-black hover:bg-[#00d4d4] rounded-lg"
        >
          <Plus className="w-4 h-4 mr-1" />
          Add Alert
        </Button>
      </div>

      {/* Add Alert Form */}
      <AnimatePresence>
        {showAddForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className={`p-4 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}
          >
            <h4 className="font-semibold mb-3">Create New Alert</h4>
            
            <div className="grid gap-3">
              {/* Alert Type */}
              <div>
                <label className="text-xs opacity-60 mb-1 block">Alert Type</label>
                <select
                  value={newAlert.type}
                  onChange={(e) => setNewAlert({ ...newAlert, type: e.target.value })}
                  className={`w-full p-2 rounded-lg ${
                    isDarkMode ? 'bg-black/30 text-white' : 'bg-white text-black'
                  }`}
                >
                  {ALERT_TYPES.map(type => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </select>
              </div>

              {/* Operator and Value */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs opacity-60 mb-1 block">Condition</label>
                  <select
                    value={newAlert.operator}
                    onChange={(e) => setNewAlert({ ...newAlert, operator: e.target.value })}
                    className={`w-full p-2 rounded-lg ${
                      isDarkMode ? 'bg-black/30 text-white' : 'bg-white text-black'
                    }`}
                  >
                    {OPERATORS.map(op => (
                      <option key={op.id} value={op.id}>{op.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs opacity-60 mb-1 block">
                    Value ({getAlertType(newAlert.type).unit})
                  </label>
                  <Input
                    type="number"
                    value={newAlert.value}
                    onChange={(e) => setNewAlert({ ...newAlert, value: e.target.value })}
                    placeholder="Enter value"
                    className={isDarkMode ? 'bg-black/30' : 'bg-white'}
                  />
                </div>
              </div>

              {/* Location */}
              <div>
                <label className="text-xs opacity-60 mb-1 block">Location (optional)</label>
                <select
                  value={newAlert.location}
                  onChange={(e) => setNewAlert({ ...newAlert, location: e.target.value })}
                  className={`w-full p-2 rounded-lg ${
                    isDarkMode ? 'bg-black/30 text-white' : 'bg-white text-black'
                  }`}
                >
                  <option value="">Current Location</option>
                  {savedLocations.map(loc => (
                    <option key={loc.id} value={loc.id}>{loc.name}</option>
                  ))}
                </select>
              </div>

              {/* Notification Options */}
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={newAlert.notify_email}
                    onChange={(e) => setNewAlert({ ...newAlert, notify_email: e.target.checked })}
                    className="rounded"
                  />
                  Email notifications
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={newAlert.notify_push}
                    onChange={(e) => setNewAlert({ ...newAlert, notify_push: e.target.checked })}
                    className="rounded"
                  />
                  Push notifications
                </label>
              </div>

              {/* Actions */}
              <div className="flex gap-2 justify-end mt-2">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setShowAddForm(false)}
                >
                  Cancel
                </Button>
                <Button 
                  size="sm" 
                  onClick={createAlert}
                  className="bg-[#00f2ea] text-black hover:bg-[#00d4d4]"
                >
                  <Save className="w-4 h-4 mr-1" />
                  Create Alert
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Alert List */}
      <div className="space-y-2">
        {alerts.length === 0 ? (
          <div className={`p-6 rounded-xl text-center ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
            <AlertTriangle className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm opacity-60">No custom alerts set up yet</p>
            <p className="text-xs opacity-40 mt-1">Create alerts to get notified about specific weather conditions</p>
          </div>
        ) : (
          alerts.map((alert) => {
            const alertType = getAlertType(alert.type);
            const operator = getOperator(alert.operator);
            const IconComponent = alertType.icon;
            
            return (
              <motion.div
                key={alert.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex items-center gap-3 p-3 rounded-xl ${
                  isDarkMode ? 'bg-white/5' : 'bg-black/5'
                } ${!alert.enabled ? 'opacity-50' : ''}`}
              >
                <IconComponent className={`w-5 h-5 ${alertType.color}`} />
                <div className="flex-1">
                  <p className="font-medium text-sm">
                    {alertType.name} {operator.symbol} {alert.value}{alertType.unit}
                  </p>
                  <p className="text-xs opacity-50">
                    {alert.location ? `At ${alert.location}` : 'Current location'}
                    {alert.notify_email && ' • Email'}
                    {alert.notify_push && ' • Push'}
                  </p>
                </div>
                <Switch
                  checked={alert.enabled}
                  onCheckedChange={(checked) => toggleAlert(alert.id, checked)}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => deleteAlert(alert.id)}
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </motion.div>
            );
          })
        )}
      </div>
    </div>
  );
}
